import { useRef, useState } from "react";
import Stepper from "../components/Stepper";
import Spinner from "../components/Spinner";
import DynamicFieldGrid from "../components/DynamicFieldGrid";
import { BackIcon, HomeIcon, CheckIcon, SendIcon, UploadIcon, CloseIcon, HistoryIcon, PlusIcon, RemarkIcon } from "../components/icons";
import { FIN_META, POST_STAGE_DEFS } from "../data/mockData";
import NOA_FIELDS_JSON from "../data/noaFormFields";
import CONTRACT_FIELDS_JSON from "../data/contractFormFields";
import { resolveImage } from "../data/assetMap";
import HistoryModal from "../components/HistoryModal";
import AddBankModal from "../components/AddBankModal";
import RemarksModal, { useRemarks } from "../components/RemarksModal";
import SendDocsChecklist from "../components/SendDocsChecklist";
import ConfirmDialog from "../components/ConfirmDialog";
import { stageState } from "../utils/tabStatus";

const STAGE_ORDER = ["send", "noa", "contract"];
const STAGE_LABELS = POST_STAGE_DEFS.map((s) => s.label);
// save_remark's tab_name for the stage a remark is added from.
const REMARK_TABS = { send: "sent_financier", noa: "noa_received", contract: "contract_signed_details" };

const thumbStyle = (d) => ({
  backgroundColor: d.kind === "PDF" ? "#FDECEC" : "#E7F1FB",
  backgroundImage: `url("${resolveImage(d.prevUrl || d.prev)}")`,
});

export default function PostApplicationPage({
  post,
  onSetStage,
  onBack,
  onGoHome,
  savedAt,
  selectedBanks,
  finTab,
  onSetFinTab,
  sent,
  sendInfo = {},
  sendLoading,
  detailsLoading,
  onPreviewSendDoc,
  sendUploadingId,
  sendRemovingId,
  onRemoveSendDoc,
  onUploadMissingDoc,
  availableBanks = [],
  maxBanks,
  canAddBanks,
  addingBanks,
  onAddBanks,
  docs,
  docsFor,
  reqLabel,
  sendExcl,
  onToggleSendExcl,
  finExtra,
  onAttachExtra,
  onOpenMail,
  onPreviewDoc,
  noaStore,
  onSetNoaField,
  noaFieldErrors,
  noaOpen,
  onSetNoaOpen,
  onSaveNoa,
  noaUpdatable = () => false,
  contractStore,
  onSetContractField,
  contractFieldErrors,
  contractOpen,
  onSetContractOpen,
  onSaveContract,
  noaMeta = {},
  noaSaving,
  noaExtracting,
  onExtractNoa,
  contractMeta = {},
  contractSaving,
  onUploadDoc,
  onUploadNoaDoc,
  onFlash,
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);
  // Uploaded file waiting on the "Remove this document?" confirmation.
  const [removeTarget, setRemoveTarget] = useState(null);
  const remarks = useRemarks(post.rawId, onFlash);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const remainingBanks = Math.max(0, (maxBanks || 0) - selectedBanks.length);
  // One hidden file input shared by every document "Upload" button on the Send page.
  const uploadInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const pickSendDoc = (doc) => {
    uploadTargetRef.current = doc;
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
      uploadInputRef.current.click();
    }
  };
  const curIdx = post.done ? 3 : Math.max(0, STAGE_ORDER.indexOf(post.stage));
  const isSendStage = post.stage === "send" && !post.done;
  const isNoaStage = post.stage === "noa" && !post.done;
  const isContractStage = post.stage === "contract" && !post.done;

  const activeFin = selectedBanks.find((b) => b.key === finTab) || selectedBanks[0] || null;
  // get_sent_to_financier's per-financier document list wins over the
  // locally-tagged docs once it has loaded; exclusions are kept per bank.
  const activeInfo = activeFin ? sendInfo[activeFin.key] : null;
  const exclKey = (d) => (activeInfo ? activeFin.key + ":" + d.id : d.id);
  const attachedRaw = activeInfo ? activeInfo.docs.filter((d) => !d.missing) : docs.filter((d) => d.req);
  // Server history plus anything sent from this session it doesn't have yet.
  const serverHistory = activeInfo?.history || [];
  const activeHistory = [activeInfo?.lastSent, activeInfo?.lastFollowUp]
    .filter((e) => e && !serverHistory.includes(e))
    .concat(serverHistory)
    .sort((x, y) => new Date(y.sent_at || 0) - new Date(x.sent_at || 0));
  const missingDocs = activeInfo ? activeInfo.docs.filter((d) => d.missing) : [];
  // sent_to_financier fails while a required document is missing, so both
  // send buttons stay disabled until every one is uploaded.
  const sendBlocked = missingDocs.length > 0;
  const includedCount = attachedRaw.filter((d) => !sendExcl[exclKey(d)]).length;
  // Per-bank send status: "sent" (history or this session), "pending" (loaded,
  // nothing sent yet) or "unknown" (its tab hasn't been opened, so
  // get_sent_to_financier hasn't been called for it).
  // get_leads' tab_status, per bank and stage: "complete" | "active" | "locked" | null.
  const tabState = (key, stage) => stageState(post.tabStatus, key, stage);
  const sendStatus = (key) =>
    sent[key] || tabState(key, "send") === "complete"
      ? "sent"
      : tabState(key, "send") === "locked"
      ? "locked"
      : sendInfo[key] || tabState(key, "send") === "active"
      ? "pending"
      : "unknown";
  const SEND_STATUS = {
    sent: { label: "Sent", dot: "var(--success)", color: "var(--success)" },
    pending: { label: "Not sent", dot: "#E0A93B", color: "#9A6700" },
    unknown: { label: "Not checked", dot: "var(--ink-300)", color: "var(--ink-500)" },
    locked: { label: "Not available", dot: "var(--ink-300)", color: "var(--ink-500)" },
  };
  const sentCount = selectedBanks.filter((b) => sendStatus(b.key) === "sent").length;
  const uncheckedCount = selectedBanks.filter((b) => sendStatus(b.key) === "unknown").length;

  const activeFinMeta = activeFin ? FIN_META[activeFin.code] || {} : {};
  const finExtraDocs = activeFin && !activeInfo
    ? (activeFinMeta.extra || []).map((name) => {
        const eid = finExtra[activeFin.key + "::" + name];
        return { name, attached: !!eid, id: eid };
      })
    : [];
  const finExtraPending = finExtraDocs.filter((d) => !d.attached).length;
  const activeSent = activeFin ? sendStatus(activeFin.key) === "sent" : false;
  const activeSendLocked = activeFin ? tabState(activeFin.key, "send") === "locked" : false;
  // NOA / Contract: the tab in view, and whether tab_status lets it be saved.
  const noaKey = noaOpen || (selectedBanks[0] && selectedBanks[0].key);
  const contractKey = contractOpen || (selectedBanks[0] && selectedBanks[0].key);
  const contractTab = contractKey ? tabState(contractKey, "contract") : null;
  const STAGE_NOTE = {
    complete: "Completed for this bank.",
    locked: "Not available for this bank yet — complete the previous stage first.",
  };
  // A received NOA stays editable until a contract is signed with that bank
  // (noaUpdatable) — saved again via "Update NOA Details".
  const noaLockReason = (key) => (noaUpdatable(key) ? null : STAGE_NOTE[tabState(key, "noa")]);
  const noaLocked = (key) => !noaUpdatable(key) && (noaMeta[key]?.editable === false || !!noaLockReason(key));
  // Once the NOA is saved as Yes or No the stage note adds nothing — the saved
  // answer already shows — so it's only shown before a decision.
  const noaLockNote = (key) => (noaMeta[key]?.state === 1 || noaMeta[key]?.state === 2 ? null : noaLockReason(key));
  // Contract signed with this bank: its NOA is final, so no Save/Update —
  // only the way on to Contract Signing.
  const contractSignedFor = (key) => !!key && (contractMeta[key]?.state === 1 || tabState(key, "contract") === "complete");
  const stageDot = (metaState, tab) =>
    metaState === 1 || (metaState == null && tab === "complete")
      ? "var(--success)"
      : metaState === 2
      ? "var(--danger)"
      : tab === "locked"
      ? "var(--ink-300)"
      : "#E0A93B";
  const remarksButton = (
    <button type="button" className="btn-outline btn-outline--cyan btn-remarks" onClick={() => setRemarksOpen(true)}>
      <RemarkIcon />
      Remarks{remarks.remarks.length > 0 ? ` (${remarks.remarks.length})` : ""}
    </button>
  );

  return (
    <div className="page-shell">
      <div className="form-header">
        <div className="container">
          <div className="form-header__row">
            <button type="button" className="form-header__icon-btn" aria-label="Back" onClick={onBack}>
              <BackIcon />
            </button>
            <div className="form-header__title">
              <h2>Post-Application · {post.loanId || ""}</h2>
              <div className="form-header__sub">{post.name || ""}</div>
            </div>
            <div className="form-header__actions">
              <span className="saved-chip">{savedAt}</span>
              <button type="button" className="home-btn" onClick={onGoHome}>
                <HomeIcon />
                <span className="home-btn__label">Home</span>
              </button>
            </div>
          </div>
          <Stepper
            steps={STAGE_LABELS}
            current={curIdx + 1}
            onGo={(n) => {
              if (n - 1 <= curIdx && !post.done) onSetStage(POST_STAGE_DEFS[n - 1].key);
            }}
            size="post"
          />
        </div>
      </div>

      <div style={{ flex: "1 0 auto" }}>
        <div className="container">
          <div className="sheet-card">
            {isSendStage && (
              <div>
                <h3 className="step-title" style={{ marginBottom: 6 }}>
                  Send to Bank
                </h3>
                <p style={{ font: "400 12px/19px 'Poppins'", color: "var(--ink-500)", margin: "0 0 18px" }}>
                  Review the attached documents and email each selected bank. Each bank is sent separately.
                </p>

                {selectedBanks.length === 0 && (
                  <div className="warning-box">No bank was selected in Step 2. Go back to the application and pick at least one bank before sending.</div>
                )}

                {selectedBanks.length > 0 && (
                  <div>
                    <div className="tabs-row">
                      {selectedBanks.map((b) => (
                        <button
                          key={b.key}
                          type="button"
                          className={"tab-pill" + (finTab === b.key ? " tab-pill--active" : "")}
                          onClick={() => onSetFinTab(b.key)}
                        >
                          <span className="tab-pill__dot" style={{ background: SEND_STATUS[sendStatus(b.key)].dot }} />
                          <span className="tab-pill__text">
                            {b.name}
                            <span className="tab-pill__status" style={{ color: SEND_STATUS[sendStatus(b.key)].color }}>
                              {SEND_STATUS[sendStatus(b.key)].label}
                            </span>
                          </span>
                        </button>
                      ))}
                      {canAddBanks && remainingBanks > 0 && (
                        <button type="button" className="tab-pill tab-pill--add" onClick={() => setAddBankOpen(true)}>
                          <PlusIcon size={14} stroke="currentColor" />
                          Add bank
                        </button>
                      )}
                    </div>

                    {activeFin && sendLoading && (
                      <div className="bank-card">
                        <div className="bank-card__body">
                          <Spinner page label="Loading bank details…" />
                        </div>
                      </div>
                    )}

                    {activeFin && !sendLoading && (
                      <div className="bank-card">
                        <div className="bank-card__body">
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ font: "400 11px/16px 'Poppins'", color: "var(--ink-500)", marginBottom: 3 }}>Bank</div>
                              <div style={{ font: "600 17px/24px 'Poppins'", color: "var(--ink-900)" }}>{activeFin.name}</div>
                            </div>
                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {activeSent && (
                                <span className="sent-badge">
                                  <CheckIcon size={13} width={3} />
                                  Sent
                                </span>
                              )}
                              <button type="button" className="history-btn" aria-label="Sent history" onClick={() => setHistoryOpen(true)}>
                                <HistoryIcon size={16} stroke="currentColor" />
                                {activeHistory.length > 0 && <span className="history-btn__count">{activeHistory.length}</span>}
                              </button>
                            </span>
                          </div>

                          <div style={{ marginTop: 16 }}>
                            <div style={{ font: "500 12px/18px 'Poppins'", color: "var(--ink-700)", marginBottom: 8 }}>Sending Mode</div>
                            <span className="send-mode-chip">
                              <UploadIcon size={14} stroke="currentColor" />
                              Email to bank · {(activeInfo ? activeInfo.raw.financier_email : activeFinMeta.email) || "—"}
                            </span>
                            {activeInfo?.raw.financier_cc_email && (
                              <div style={{ font: "400 11px/16px 'Poppins'", color: "var(--ink-500)", marginTop: 6, wordBreak: "break-word" }}>
                                CC · {activeInfo.raw.financier_cc_email}
                              </div>
                            )}
                          </div>

                          <div style={{ marginTop: 20 }}>
                            <div className="docs-attached__head">
                              <div className="docs-attached__title">Documents</div>
                              <span className="docs-attached__badge">
                                {includedCount} of {attachedRaw.length} selected
                                {missingDocs.length > 0 && " · " + missingDocs.length + " required pending"}
                              </span>
                            </div>
                            {activeInfo && (
                              <SendDocsChecklist
                                cats={activeInfo.cats || []}
                                isIncluded={(d) => !sendExcl[exclKey(d)]}
                                onToggleInclude={(d) => onToggleSendExcl(exclKey(d))}
                                onPreview={onPreviewSendDoc}
                                onUpload={pickSendDoc}
                                uploadingId={sendUploadingId}
                                onRemove={(d) => setRemoveTarget({ key: activeFin.key, file: d })}
                                removingId={sendRemovingId}
                                // Once sent to this bank its documents are final — view and
                                // include/exclude only, no upload or remove.
                                readOnly={activeSent}
                              />
                            )}
                            {!activeInfo && attachedRaw.length > 0 && (
                              <div className="docs-attached__list">
                                {attachedRaw.map((d) => {
                                  const inc = !sendExcl[exclKey(d)];
                                  return (
                                    <div key={d.id} className={"docs-attached__row" + (!inc ? " docs-attached__row--excluded" : "")}>
                                      <button
                                        type="button"
                                        className={"docs-attached__check" + (inc ? " docs-attached__check--on" : "")}
                                        aria-label="Include in send"
                                        onClick={() => onToggleSendExcl(exclKey(d))}
                                      >
                                        {inc && <CheckIcon size={12} stroke="#fff" width={3} />}
                                      </button>
                                      <span className="docs-attached__thumb" style={thumbStyle(d)} />
                                      <span style={{ flex: 1, minWidth: 0 }}>
                                        <span className={"docs-attached__name" + (!inc ? " docs-attached__name--excluded" : "")}>{d.name}</span>
                                        <span style={{ display: "block", font: "400 10px/15px 'Poppins'", color: "var(--ink-500)", marginTop: 2 }}>
                                          {activeInfo ? (
                                            <>
                                              {d.section}
                                              {d.docType ? " · " + d.docType.charAt(0).toUpperCase() + d.docType.slice(1) : ""}
                                            </>
                                          ) : (
                                            <>
                                              {reqLabel(d.req)}
                                              {d.bankName ? " · " + d.bankName : d.govType ? " · " + d.govType : ""}
                                            </>
                                          )}
                                        </span>
                                      </span>
                                      <button type="button" className="btn-preview" onClick={() => (activeInfo ? onPreviewSendDoc(d) : onPreviewDoc(d.id))}>
                                        View
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <input
                              ref={uploadInputRef}
                              type="file"
                              accept="image/*,application/pdf"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                if (file && uploadTargetRef.current) onUploadMissingDoc(activeFin.key, uploadTargetRef.current, file);
                              }}
                            />

                            {finExtraDocs.length > 0 && (
                              <div>
                                <div className="extra-docs-label">{activeFin.name}-specific mandatory documents</div>
                                <div className="extra-docs__list">
                                  {finExtraDocs.map((d) => (
                                    <div key={d.name} className="extra-doc-row">
                                      <span className={"extra-doc-row__tick" + (d.attached ? " extra-doc-row__tick--on" : "")}>
                                        {d.attached && <CheckIcon size={11} stroke="#fff" width={3} />}
                                      </span>
                                      <span className="extra-doc-row__name">{d.name}</span>
                                      <span
                                        className="extra-doc-row__status"
                                        style={{
                                          background: d.attached ? "rgba(5,172,86,0.12)" : "#FFF3D6",
                                          color: d.attached ? "var(--success)" : "#9A6700",
                                        }}
                                      >
                                        {d.attached ? "Attached" : "Mandatory"}
                                      </span>
                                      {d.attached ? (
                                        <button type="button" className="btn-preview" onClick={() => onPreviewDoc(d.id)}>
                                          View
                                        </button>
                                      ) : (
                                        <button type="button" className="extra-doc-row__attach" onClick={() => onAttachExtra(activeFin.key, d.name)}>
                                          Attach
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="send-actions">
                            {!activeSent && (
                              <button
                                type="button"
                                className="send-actions__primary"
                                onClick={() => onOpenMail("email", activeFin.key)}
                                disabled={sendBlocked || activeSendLocked}
                              >
                                <SendIcon size={16} stroke="#fff" />
                                {"Send to " + activeFin.name}
                              </button>
                            )}
                            {activeSent && (
                              <button
                                type="button"
                                className="send-actions__followup"
                                onClick={() => onOpenMail("followup", activeFin.key)}
                                disabled={sendBlocked}
                              >
                                Send follow-up to {activeFin.name}
                              </button>
                            )}
                          </div>
                          {activeSendLocked && <div className="send-pending-note">{STAGE_NOTE.locked}</div>}
                          {missingDocs.length > 0 && (
                            <div className="send-pending-note">
                              {missingDocs.length} required document{missingDocs.length === 1 ? " is" : "s are"} not uploaded. Upload{" "}
                              {missingDocs.length === 1 ? "it" : "them"} above to send to {activeFin.name}.
                            </div>
                          )}
                          {finExtraPending > 0 && <div className="send-pending-note">Attach all mandatory documents before sending.</div>}
                        </div>
                      </div>
                    )}

                    <div className="send-summary-row">
                      <span className="send-summary-row__text">
                        {sentCount} of {selectedBanks.length} bank{selectedBanks.length === 1 ? "" : "s"} sent
                        {uncheckedCount > 0 && " · " + uncheckedCount + " not checked yet (open the tab to check)"}
                      </span>
                      {remarksButton}
                      {/* Only once this bank has been sent to — opens NOA on the same bank. */}
                      {activeFin && activeSent && (
                        <button
                          type="button"
                          className="send-summary-row__cta"
                          onClick={() => {
                            onSetNoaOpen(activeFin.key);
                            onSetStage("noa");
                          }}
                        >
                          Continue to NOA for {activeFin.name}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isNoaStage && (
              <div>
                <h3 className="step-title">NOA Received</h3>
                <div className="tabs-row">
                  {selectedBanks.map((b) => {
                    const active = (noaOpen || (selectedBanks[0] && selectedBanks[0].key)) === b.key;
                    const dot = stageDot(noaMeta[b.key]?.state, tabState(b.key, "noa"));
                    return (
                      <button key={b.key} type="button" className={"tab-pill" + (active ? " tab-pill--active" : "")} onClick={() => onSetNoaOpen(b.key)}>
                        <span className="tab-pill__dot" style={{ background: dot }} />
                        {b.name}
                      </button>
                    );
                  })}
                </div>
                {selectedBanks
                  .filter((b) => (noaOpen || (selectedBanks[0] && selectedBanks[0].key)) === b.key)
                  .map((b) => (
                    <div className="bank-card" key={b.key}>
                      <div className="bank-card__head">
                        <span>{b.name}</span>
                      </div>
                      <div className="bank-card__body">
                        {noaLockNote(b.key) && <div className="send-pending-note">{noaLockNote(b.key)}</div>}
                        {detailsLoading ? (
                          <Spinner page label="Loading NOA details…" />
                        ) : (
                          <DynamicFieldGrid
                            fields={NOA_FIELDS_JSON}
                            values={noaStore[b.key] || {}}
                            onChange={(name, val) => onSetNoaField(b.key, name, val)}
                            errors={(noaFieldErrors && noaFieldErrors[b.key]) || {}}
                            readOnly={noaLocked(b.key)}
                            onUpload={onUploadNoaDoc || onUploadDoc}
                            showRequired
                          />
                        )}
                        {/* NOA can arrive as the email body only — the user attaches a screenshot
                            of it above, and OCR pre-fills the fields from that. */}
                        {!detailsLoading &&
                          noaStore[b.key]?.noa_received === "Yes" &&
                          !noaLocked(b.key) && (
                            <div className="noa-extract">
                              <span className="noa-extract__hint">
                                {noaExtracting
                                  ? "Reading the NOA details…"
                                  : "Details are read automatically after upload — got the NOA in the email body? Upload a screenshot of the email. Review the fields before saving."}
                              </span>
                              <button
                                type="button"
                                className="btn-outline btn-outline--cyan"
                                onClick={() => onExtractNoa()}
                                disabled={noaExtracting || !/^https?:/.test(String(noaStore[b.key]?.noa_upload || ""))}
                              >
                                {noaExtracting ? "Reading…" : "Extract again"}
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                  ))}

                <div className="post-stage-actions">
                  <button type="button" className="btn-outline" onClick={() => onSetStage("send")}>
                    Back
                  </button>
                  {remarksButton}
                  <span style={{ flex: 1 }} />
                  {selectedBanks.some((b) => noaMeta[b.key]?.state === 1 || tabState(b.key, "noa") === "complete") && (
                    <button type="button" className="btn-outline" onClick={() => onSetStage("contract")}>
                      Continue to Contract Signing
                    </button>
                  )}
                  {!contractSignedFor(noaKey) && (
                    <button type="button" className="btn-cta" onClick={onSaveNoa} disabled={noaSaving || detailsLoading || noaLocked(noaKey)}>
                      {noaSaving ? "Saving…" : noaUpdatable(noaKey) ? "Update NOA Details" : "Save NOA"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {isContractStage && (
              <div>
                <h3 className="step-title">Contract Signing</h3>
                <div className="tabs-row">
                  {selectedBanks.map((b) => {
                    const active = (contractOpen || (selectedBanks[0] && selectedBanks[0].key)) === b.key;
                    const dot = stageDot(contractMeta[b.key]?.state, tabState(b.key, "contract"));
                    return (
                      <button key={b.key} type="button" className={"tab-pill" + (active ? " tab-pill--active" : "")} onClick={() => onSetContractOpen(b.key)}>
                        <span className="tab-pill__dot" style={{ background: dot }} />
                        {b.name}
                      </button>
                    );
                  })}
                </div>
                {selectedBanks
                  .filter((b) => (contractOpen || (selectedBanks[0] && selectedBanks[0].key)) === b.key)
                  .map((b) => (
                    <div className="bank-card" key={b.key}>
                      <div className="bank-card__head">
                        <span>{b.name}</span>
                      </div>
                      <div className="bank-card__body">
                        {STAGE_NOTE[tabState(b.key, "contract")] && (
                          <div className="send-pending-note">{STAGE_NOTE[tabState(b.key, "contract")]}</div>
                        )}
                        {detailsLoading ? (
                          <Spinner page label="Loading contract details…" />
                        ) : (
                          <DynamicFieldGrid
                            fields={CONTRACT_FIELDS_JSON}
                            values={contractStore[b.key] || {}}
                            onChange={(name, val) => onSetContractField(b.key, name, val)}
                            errors={(contractFieldErrors && contractFieldErrors[b.key]) || {}}
                            readOnly={contractMeta[b.key]?.editable === false || !!STAGE_NOTE[tabState(b.key, "contract")]}
                            onUpload={onUploadDoc}
                            showRequired
                            uploadsLast
                          />
                        )}
                      </div>
                    </div>
                  ))}

                <div className="post-stage-actions">
                  <button type="button" className="btn-outline" onClick={() => onSetStage("noa")}>
                    Back
                  </button>
                  {remarksButton}
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="btn-cta"
                    onClick={onSaveContract}
                    disabled={contractSaving || detailsLoading || contractMeta[contractKey]?.editable === false || !!STAGE_NOTE[contractTab]}
                  >
                    {contractSaving ? "Saving…" : "Record Signing"}
                  </button>
                </div>
              </div>
            )}

            {post.done && (
              <div className="post-done">
                {post.declined ? (
                  <div>
                    <span className="post-done__icon post-done__icon--declined">
                      <CloseIcon size={30} stroke="#fff" />
                    </span>
                    <h3 className="post-done__title">{post.declinedAt === "contract" ? "Contract signing declined" : "NOA declined"}</h3>
                    <p className="post-done__body">
                      {post.declinedAt === "contract"
                        ? "The contract decline has been recorded for every financier on this application. Track it in the Mobile SFA app."
                        : "The decline has been recorded. You can re-send this application to another bank from the Send stage, or track it in the Mobile SFA app."}
                    </p>
                  </div>
                ) : (
                  <div>
                    <span className="post-done__icon post-done__icon--approved">
                      <CheckIcon size={36} stroke="#fff" width={3} />
                    </span>
                    <h3 className="post-done__title post-done__title--approved">Contract signed</h3>
                    <p className="post-done__body post-done__body--approved">
                      The application has been sent to the bank, the NOA is recorded and the signed contract is uploaded. Disbursement is now tracked in the Mobile SFA app.
                    </p>
                    <div className="post-done__chip">
                      <UploadIcon size={18} />
                      Continue disbursement tracking in the Mobile SFA app
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button type="button" className="post-done__back" onClick={onBack}>
                    Back to Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <AddBankModal
        open={addBankOpen && isSendStage}
        banks={availableBanks}
        remaining={remainingBanks}
        max={maxBanks}
        saving={addingBanks}
        onClose={() => setAddBankOpen(false)}
        onAdd={async (keys) => {
          if (await onAddBanks(keys)) setAddBankOpen(false);
        }}
      />
      <ConfirmDialog
        confirm={removeTarget && { kind: "remove", name: removeTarget.file.name }}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => {
          onRemoveSendDoc(removeTarget.key, removeTarget.file);
          setRemoveTarget(null);
        }}
      />
      <RemarksModal
        open={remarksOpen && !post.done}
        remarks={remarks.remarks}
        loading={remarks.loading}
        saving={remarks.saving}
        onSubmit={(text) => remarks.add(REMARK_TABS[post.stage] || post.stage, text)}
        onClose={() => setRemarksOpen(false)}
      />
      <HistoryModal
        open={historyOpen && isSendStage && !!activeFin}
        bankName={activeFin?.name || ""}
        history={activeHistory}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
